import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, AlertTriangle, Calendar } from 'lucide-react';
import { toast } from 'sonner';

import API from '../api_config';

const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
};

export default function ReportsPage() {
  const [overdueAccounts, setOverdueAccounts] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOverdueAccounts();
  }, [days]);

  const fetchOverdueAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/overdue-accounts?days=${days}`);
      setOverdueAccounts(response.data);
    } catch (error) {
      toast.error('Error al cargar reporte');
    } finally {
      setLoading(false);
    }
  };

  const totalOverdue = overdueAccounts.reduce((sum, acc) => sum + acc.balance, 0);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-red-500 bg-red-50 dark:bg-red-900/20';
      case 'medium':
        return 'border-orange-500 bg-orange-50 dark:bg-orange-900/20';
      default:
        return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center space-x-3">
        <div className="p-3 bg-red-600 rounded-2xl">
          <FileText className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Reportes - Cuentas Vencidas</h1>
          <p className="text-muted-foreground mt-1">
            Clientes con pagos atrasados
          </p>
        </div>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-end space-x-4">
            <div className="space-y-2 flex-1">
              <Label>Días sin pago</Label>
              <Input
                type="number"
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value))}
                min="1"
                data-testid="days-filter-input"
              />
            </div>
            <Button onClick={fetchOverdueAccounts} disabled={loading}>
              <Calendar className="h-4 w-4 mr-2" />
              {loading ? 'Cargando...' : 'Actualizar'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border-red-200 dark:border-red-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Vencido
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-red-600 dark:text-red-400">
              {formatCLP(totalOverdue)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Cuentas Vencidas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{overdueAccounts.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Promedio por Cliente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {overdueAccounts.length > 0
                ? formatCLP(totalOverdue / overdueAccounts.length)
                : formatCLP(0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overdue Accounts List */}
      <div className="space-y-4">
        {overdueAccounts.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay cuentas vencidas en los últimos {days} días</p>
            </CardContent>
          </Card>
        ) : (
          overdueAccounts.map((account) => (
            <Card
              key={account.id}
              className={`hover:shadow-lg transition-shadow border-2 ${getPriorityColor(account.priority)}`}
              data-testid={`overdue-account-${account.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <AlertTriangle
                        className={`h-6 w-6 ${
                          account.priority === 'high'
                            ? 'text-red-600'
                            : account.priority === 'medium'
                            ? 'text-orange-600'
                            : 'text-yellow-600'
                        }`}
                      />
                      <div>
                        <h3 className="font-semibold text-xl">
                          {account.customer?.name || 'Cliente desconocido'}
                        </h3>
                        {account.customer?.rut && (
                          <p className="text-sm text-muted-foreground font-mono">
                            RUT: {account.customer.rut}
                          </p>
                        )}
                      </div>
                      <span
                        className={`px-3 py-1 text-xs rounded-full font-medium ${
                          account.priority === 'high'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : account.priority === 'medium'
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}
                      >
                        {account.priority === 'high'
                          ? 'Prioridad Alta'
                          : account.priority === 'medium'
                          ? 'Prioridad Media'
                          : 'Prioridad Baja'}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Saldo Pendiente</p>
                        <p className="text-2xl font-bold font-mono text-red-600 dark:text-red-400">
                          {formatCLP(account.balance)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Días Vencidos</p>
                        <p className="text-2xl font-bold">{account.days_overdue}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Transacciones</p>
                        <p className="text-2xl font-bold">{account.transactions?.length || 0}</p>
                      </div>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs font-semibold mb-2">Último Pago:</p>
                      <p className="text-sm">
                        {account.last_payment_date
                          ? new Date(account.last_payment_date).toLocaleDateString('es-CL', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          : 'Sin pagos registrados'}
                      </p>
                    </div>
                  </div>

                  <div className="ml-6 text-right">
                    {account.customer?.phone && (
                      <div className="mb-2">
                        <p className="text-xs text-muted-foreground">Contacto</p>
                        <p className="text-sm font-medium">{account.customer.phone}</p>
                      </div>
                    )}
                    {account.customer?.email && (
                      <p className="text-xs text-muted-foreground">{account.customer.email}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}